import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { flowRight as compose, get } from 'lodash';
import { Field, withFormik } from 'formik';
import moment from 'moment';
import {
  Text,
  BodyTitle,
  Icon,
  Box,
  Flex,
  Button,
} from '@firsttable/web-components/atoms';
import Form from '@firsttable/web-components/molecules/Form';
import FormField from '@firsttable/web-components/molecules/FormField/FormField';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { errorMessage, freeCreditAmount } from '@firsttable/functions';
import gql from 'graphql-tag';
import { graphql as apolloGraphql } from '@apollo/client/react/hoc';
import { withAlert } from 'react-alert';
import { useLocation } from '@reach/router';
import Cookies from 'js-cookie';
import AuthContext from '../../../context/AuthContext';
import withLocation from '../../../hocs/location';
import FacebookLoginButton from '../Auth/FacebookLoginButton';
import BookRestaurant from '../BookRestaurant';
import RegionField from './Fields/RegionField';

const UPDATE_USER_CITY_SUBSCRIPTIONS_QUERY = gql`
  mutation updateUserCitySubscriptions($cityId: [Int]!, $subscribe: Boolean!) {
    updateUserCitySubscriptions(cityId: $cityId, subscribe: $subscribe) {
      id
    }
  }
`;

export const GET_PROMO_SUBSCRIPTIONS = gql`
  query getUserPromoSubscriptions($siteIds: [Int]) {
    user: UserSubscription {
      countries(siteIds: $siteIds) {
        edges {
          node {
            id
            country
            siteId
            subscriptions {
              edges {
                node {
                  id
                  title
                  isChecked
                }
              }
            }
          }
        }
      }
    }
  }
`;

const PromoFormContent = ({ isSubmitting, city }) => {
  const launchDate = get(city, 'launchDate');
  return (
    <>
      <BodyTitle size="l" mt={0} mb="8px" textAlign="center">
        Coming soon
      </BodyTitle>
      <span>
        <Text mb="10px" textAlign="center">
          {launchDate
            ? `Local food lovers can book our early bird dining deals from ${moment(
                launchDate,
              ).format('do MMMM')}.`
            : 'Local food lovers will soon be able to book our early bird dining deals.'}
        </Text>
        <Flex justifyContent="center">
          <Field
            component={FormField}
            type="checkbox"
            name="citySubscribed"
            label={`Subscribe me to updates about First Table in ${city.menuTitle}`}
            m="30px 0 20px 0"
          />
        </Flex>
        <Box mt={['20px', '30px']} textAlign="center">
          <Button wide size="l" type="submit" isLoading={isSubmitting}>
            Update subscription
          </Button>
        </Box>
      </span>
    </>
  );
};

PromoFormContent.propTypes = {
  city: PropTypes.shape(),
  isSubmitting: PropTypes.bool,
};
PromoFormContent.defaultProps = {
  isSubmitting: false,
};

/**
 * Will conditionally show promo code field if set in the url e.g. ?code=WELCOMEIE&City=28
 * @param regions
 * @param handleSubmit
 * @param values
 * @returns {*}
 * @constructor
 */
const HomeSignInForm = ({
  handleSubmit,
  values,
  isCityComingSoon,
  ...props
}) => {
  const { pathname } = useLocation();
  const { isLoggedIn } = useContext(AuthContext);
  const isUserLoggedIn = isLoggedIn();
  const { siteConfig } = useStaticQuery(graphql`
    query getSignInCredit {
      siteConfig {
        currency
        freeCreditAmount
      }
    }
  `);

  if (isCityComingSoon && isUserLoggedIn) {
    return (
      <Form onSubmit={handleSubmit}>
        <Box textAlign="center" mb="13px">
          <Icon
            name="logo-bird"
            color="brand.blue"
            width={isUserLoggedIn ? 50 : 40}
            height={isUserLoggedIn ? 50 : 40}
            viewBox="0 0 29 30"
          />
        </Box>
        <PromoFormContent
          isUserLoggedIn
          isCityComingSoon
          values={values}
          {...props}
        />
      </Form>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Box textAlign="center" mb="13px">
        <Icon
          name="logo-bird"
          color="brand.blue"
          width={isUserLoggedIn ? 50 : 40}
          height={isUserLoggedIn ? 50 : 40}
          viewBox="0 0 29 30"
        />
      </Box>
      {isUserLoggedIn ? (
        <>
          <BodyTitle size="m" textAlign="center" mb={['m', 'l']}>
            Welcome to First Table
          </BodyTitle>
          <BookRestaurant />
        </>
      ) : (
        <>
          <BodyTitle size="l" mb="8px" textAlign="center">
            Create an account
          </BodyTitle>
          <Text mb="35px" textAlign="center">
            {isCityComingSoon
              ? 'Sign up before we launch and get'
              : 'Sign up now and get'}{' '}
            {freeCreditAmount(siteConfig, false)} off your first booking fee.
          </Text>
          <RegionField />

          <Field
            component={FormField}
            mb="25px"
            name="promoCode"
            placeholder="Enter your promo code if you have one"
            label="Promo Code"
            size="s"
            type="text"
          />

          <FacebookLoginButton
            signUpData={values}
            onAfterLogin={() => navigate(`${pathname}?confirmed=1`)}
          />
        </>
      )}
    </Form>
  );
};

HomeSignInForm.propTypes = {
  values: PropTypes.shape(),
  handleSubmit: PropTypes.func.isRequired,
  isCityComingSoon: PropTypes.bool,
};

HomeSignInForm.defaultProps = {
  isCityComingSoon: false,
};

export default compose(
  apolloGraphql(UPDATE_USER_CITY_SUBSCRIPTIONS_QUERY, {
    name: 'updateUserCitySubscriptions',
  }),
  apolloGraphql(GET_PROMO_SUBSCRIPTIONS, {
    alias: 'withUserComingSoonSubscriptions',
    // only run this query if it's a city promo page
    skip: ({ isCityComingSoon, isUserLoggedIn }) =>
      !(isCityComingSoon && isUserLoggedIn),
    props: ({ data: { user } }) => {
      if (!user) {
        return {
          usersSubscriptions: null,
        };
      }
      const {
        countries: {
          edges: [usersSubscriptions],
        },
      } = user;
      return {
        usersSubscriptions,
      };
    },
    options: () => ({
      variables: {
        siteIds: [+process.env.GATSBY_SITE_ID],
      },
      fetchPolicy: 'network-only',
      ssr: false,
    }),
  }),
  withAlert(),
  withLocation,
  withFormik({
    enableReinitialize: true,
    // validationSchema: () => Yup.object().shape({
    //   cityId: Yup.string().required(),
    // }),
    mapPropsToValues: ({ search, cityId, usersSubscriptions }) => {
      const { City, code } = search;
      let subscriptionList = [];

      if (usersSubscriptions) {
        const { node: country } = usersSubscriptions;
        subscriptionList = country.subscriptions.edges.filter(
          ({ node }) => node.id === cityId && node.isChecked,
        );
      }

      return {
        cityId: +City || cityId,
        promoCode: code || '',
        citySubscribed: subscriptionList.length > 0,
      };
    },
    handleSubmit: async (
      values,
      { props: { updateUserCitySubscriptions, cityId, alert } },
    ) => {
      try {
        await updateUserCitySubscriptions({
          variables: {
            cityId: [cityId],
            subscribe: values.citySubscribed,
          },
        });
        if (Cookies.get('code')) {
          Cookies.remove('code');
        }
        alert.success('Subscription updated');
      } catch (error) {
        alert.error(errorMessage(error));
      }
    },
  }),
)(HomeSignInForm);
